/**
 * Preference, availability and copy for the composer's sources menus, over
 * the rows in `composer-sources-model.ts`. The web menu and the phone's "+"
 * sheet and "@" suggestion list all read this hook.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AgentArtifactCapabilityMode,
  AgentCapabilityMode,
} from '@tabbio/types';
import { useAgentCapabilities } from '@/hooks/useAgentCapabilities';
import {
  AGENT_CAPABILITY_MODES,
  applyAgentCapabilityChange,
  resolveTaskCapabilityControls,
  type AgentCapabilitiesPreference,
  type AgentCapabilityKey,
} from '@/lib/agent-capabilities-preference';
import {
  composerSourceRows,
  nextComposerMode,
  type ComposerSourceRow,
  type ComposerSourcesContext,
} from './composer-sources-model';

export {
  filterComposerRows,
  type ComposerSourceKey,
  type ComposerSourceRow,
} from './composer-sources-model';

/**
 * Preference, availability and copy for the sources menu. Uncontrolled
 * (chat), a click writes the sticky preference every chat composer reads.
 * Controlled (`value` + `onChange`), the picks belong to the caller, the same
 * contract as `CapabilityChips`: a Task's controls, whose Result leaves out
 * Page until Tasks can make pages (a follow-up).
 */
export function useComposerSources(options: {
  capabilitiesEnabled: boolean;
  value?: AgentCapabilitiesPreference;
  onChange?: (next: AgentCapabilitiesPreference) => void;
}) {
  const { capabilitiesEnabled, value, onChange } = options;
  const { t } = useTranslation('common');
  const {
    capabilities,
    controls: stickyControls,
    setCapability: setStickyCapability,
  } = useAgentCapabilities();
  const controlled = value !== undefined && onChange !== undefined;
  const controls = useMemo(
    () =>
      controlled
        ? resolveTaskCapabilityControls(value, capabilities)
        : stickyControls,
    [capabilities, controlled, stickyControls, value],
  );

  const setCapability = useCallback(
    <K extends AgentCapabilityKey>(key: K, next: AgentCapabilitiesPreference[K]) => {
      if (controlled) onChange(applyAgentCapabilityChange(value, key, next));
      else setStickyCapability(key, next);
    },
    [controlled, onChange, setStickyCapability, value],
  );

  const rowsFor = useCallback(
    (context: ComposerSourcesContext) =>
      composerSourceRows(controls, context, capabilitiesEnabled),
    [capabilitiesEnabled, controls],
  );

  const titleFor = useCallback(
    (row: ComposerSourceRow) => {
      if (row.key === 'attach') return t('chat.capabilities.addFiles');
      // An "@" row is titled with the label it writes into the draft.
      if (row.mention) return t(`chat.composer.mentions.${row.mention}`);
      return t(`chat.composer.sources.${row.key}.title`);
    },
    [t],
  );

  const descriptionFor = useCallback(
    (row: ComposerSourceRow) => {
      if (row.key === 'attach') return t('chat.composer.sources.attach.description');
      if (row.action === 'upgrade') return t('chat.capabilities.image.lockedHint');
      if (row.mention) return t(`chat.composer.mentionHints.${row.mention}`);
      return t(`chat.composer.sources.${row.key}.description`);
    },
    [t],
  );

  /** The sticky state a "+" row shows on its right, or null for none. */
  const stateFor = useCallback(
    (row: ComposerSourceRow): string | null => {
      if (row.action !== 'cycle') return null;
      if (row.key === 'research') {
        return t(`chat.capabilities.states.${controls.research.mode}`);
      }
      if (row.key === 'image') {
        return t(`chat.capabilities.states.${controls.image.mode}`);
      }
      if (row.key === 'result') {
        return t(`chat.capabilities.result.options.${controls.artifact.mode}.title`);
      }
      return null;
    },
    [controls, t],
  );

  /** Whether a "+" row is set to anything other than Auto. */
  const isSet = useCallback(
    (row: ComposerSourceRow) => {
      if (row.action !== 'cycle') return false;
      if (row.key === 'research') return controls.research.mode !== 'auto';
      if (row.key === 'image') return controls.image.mode !== 'auto';
      if (row.key === 'result') return controls.artifact.mode !== 'auto';
      return false;
    },
    [controls],
  );

  /** A click on a "+" capability row: its next state. */
  const cycle = useCallback(
    (row: ComposerSourceRow) => {
      if (row.key === 'research') {
        setCapability(
          'research',
          nextComposerMode<AgentCapabilityMode>(controls.research.mode, AGENT_CAPABILITY_MODES),
        );
      } else if (row.key === 'image') {
        setCapability(
          'image',
          nextComposerMode<AgentCapabilityMode>(controls.image.mode, AGENT_CAPABILITY_MODES),
        );
      } else if (row.key === 'result') {
        setCapability(
          'artifact',
          nextComposerMode<AgentArtifactCapabilityMode>(
            controls.artifact.mode,
            controls.artifact.options,
          ),
        );
      }
    },
    [controls, setCapability],
  );

  return useMemo(
    () => ({
      controls,
      setCapability,
      rowsFor,
      titleFor,
      descriptionFor,
      stateFor,
      isSet,
      cycle,
    }),
    [controls, cycle, descriptionFor, isSet, rowsFor, setCapability, stateFor, titleFor],
  );
}
